/**
 * useVoiceInput - React hook for voice input lifecycle.
 *
 * Manages the ElevenLabs WebSocket connection, microphone access,
 * and transcript delivery within React components.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchWithDefaults } from '@/lib/http/client';

import { ElevenLabsWebSocket } from './ElevenLabsWebSocket';
import type { VoiceInputStatus } from './types';

interface UseVoiceInputOptions {
  /** Org ID for token generation */
  orgId: number | null;
  /** Callback for transcript updates (partial and final) */
  onTranscript: (text: string, isFinal: boolean) => void;
}

interface UseVoiceInputReturn {
  /** Current voice input status */
  status: VoiceInputStatus;
  /** Error message (null when no error) */
  error: string | null;
  /** Start recording audio */
  startRecording: () => Promise<void>;
  /** Stop recording audio */
  stopRecording: () => void;
  /** Whether voice input is supported in this browser */
  isSupported: boolean;
}

/**
 * React hook that manages voice input lifecycle.
 *
 * Fetches ElevenLabs token from backend, creates WebSocket connection,
 * and delivers transcripts to caller.
 *
 * Usage:
 * ```tsx
 * const { status, error, startRecording, stopRecording, isSupported } = useVoiceInput({
 *   orgId: currentOrg?.id ?? null,
 *   onTranscript: (text, isFinal) => {
 *     if (isFinal) {
 *       // Handle committed transcript
 *     } else {
 *       // Show partial transcript
 *     }
 *   },
 * });
 * ```
 */
export function useVoiceInput({
  orgId,
  onTranscript,
}: UseVoiceInputOptions): UseVoiceInputReturn {
  const [status, setStatus] = useState<VoiceInputStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<ElevenLabsWebSocket | null>(null);
  const mountedRef = useRef(true);
  const startingRef = useRef(false);

  // Check if browser supports voice input
  const isSupported =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

  const stopRecording = useCallback(() => {
    wsRef.current?.stopRecording();
    wsRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    if (!orgId) {
      setError('No organization selected');
      setStatus('error');
      return;
    }

    if (!isSupported) {
      setError('Voice input not supported in this browser');
      setStatus('error');
      return;
    }

    // Guard against concurrent starts using ref (avoids stale closure)
    if (startingRef.current || wsRef.current) {
      return;
    }

    // Set connecting state before any async work to prevent re-entry
    startingRef.current = true;
    setStatus('connecting');
    setError(null);

    try {
      // Fetch ElevenLabs token from backend with 5 second timeout
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/voice/token/`,
        {
          method: 'GET',
          signal: AbortSignal.timeout(5000),
        },
        5000
      );

      // Short-circuit if unmounted during async fetch
      if (!mountedRef.current) {
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch voice token');
      }

      const data = (await response.json()) as { token: string };

      // Short-circuit if unmounted during json parse
      if (!mountedRef.current) {
        return;
      }

      // Create ElevenLabs WebSocket with callbacks
      const ws = new ElevenLabsWebSocket({
        token: data.token,
        onPartialTranscript: (text: string) => {
          onTranscript(text, false);
        },
        onCommittedTranscript: (text: string) => {
          onTranscript(text, true);
        },
        onStatusChange: (newStatus: VoiceInputStatus) => {
          setStatus(newStatus);
          // Reset wsRef when recording ends to allow retries.
          // On error: destroy with preserveStatus=true to release resources (mic, WebSocket,
          // audio context) without transitioning to idle, which would mask the error state.
          // On idle: the socket has already cleaned up via stopRecording/onclose.
          if (newStatus === 'error') {
            wsRef.current?.destroy(true);
            wsRef.current = null;
          } else if (newStatus === 'idle') {
            wsRef.current = null;
          }
        },
        onError: setError,
      });

      wsRef.current = ws;

      // Start recording
      await ws.startRecording();
      startingRef.current = false;
    } catch (err) {
      startingRef.current = false;
      wsRef.current = null; // Reset so startRecording can be called again
      // Don't update state if unmounted
      if (!mountedRef.current) {
        return;
      }
      // User-friendly error for fetch failures
      if (err instanceof Error) {
        if (err.name === 'AbortError' || err.message.includes('timeout')) {
          setError('Could not connect to voice service. Please try again.');
        } else {
          setError('Could not connect to voice service. Please try again.');
        }
      } else {
        setError('Could not connect to voice service. Please try again.');
      }
      setStatus('error');
    }
  }, [orgId, onTranscript, isSupported]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      wsRef.current?.destroy();
      wsRef.current = null;
    };
  }, []);

  return {
    status,
    error,
    startRecording,
    stopRecording,
    isSupported,
  };
}
